import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import Registration from './components/Registration';
import config, { initWorkerBase, getWorkerBase } from './config';
import errorTracker from './utils/errorTracker';
import logger from './utils/logger';
import { get_toggle_state, set_toggle_state } from './utils/toggle_cache';

// Path-based routing helpers (clean URLs)
const sectionIds = ['A','B','C','D','E'];
const pageToPath = (pageId, opts = {}) => {
  switch(pageId){
    case 'registration': {
      const step = Number(opts.qs_step || 1);
      const stepSlug = step === 2 ? 'quick-launch-2' : step === 3 ? 'quick-launch-3' : 'quick-launch-1';
      return `/quick-start/${stepSlug}`;
    }
    case 'home': return '/free-plan';
    case 'pricing': return '/pricing';
    default:
      if (sectionIds.includes(pageId)) return `/section-${pageId.toLowerCase()}`;
      return `/${pageId}`;
  }
};
const adminTabToPath = (tabId) => {
  switch(tabId){
    case 'analytics': return '/admin/database-performance';
    case 'health': return '/admin/system-health';
    case 'users': return '/admin/user-management';
    case 'console': return '/admin/console-errors';
    case 'general':
    default: return '/admin/general';
  }
};
const parsePath = (pathname) => {
  // Admin paths
  if (pathname.startsWith('/admin')){
    const parts = pathname.split('/').filter(Boolean);
    const slug = parts[1] || 'general';
    const tabMap = {
      'database-performance':'analytics',
      'system-health':'health',
      'user-management':'users',
      'console-errors':'console',
      'general':'general'
    };
    return { page: 'admin', tab: tabMap[slug] || 'general' };
  }
  // Frontend paths
  if (pathname.startsWith('/quick-start')){
    const parts = pathname.split('/').filter(Boolean);
    // parts[0] === 'quick-start', parts[1] may be quick-launch-N
    let qs_step = 1;
    if (parts[1] && /^quick-launch-(1|2|3)$/i.test(parts[1])){
      qs_step = Number(parts[1].split('-').pop());
    }
    return { page: 'registration', tab: null, qs_step };
  }
  const map = {
    '/free-plan':'home',
    '/pricing':'pricing',
    '/section-a':'A', '/section-b':'B', '/section-c':'C', '/section-d':'D', '/section-e':'E'
  };
  return { page: map[pathname] || 'registration', tab: null, qs_step: 1 };
};

// Admin Dashboard Component
const AdminDashboard = ({ externalActiveTab, onExternalTabChange, hideTabs = false }) => {
  const [adminData, setAdminData] = useState({
    totalZones: 0,
    totalCustomers: 0,
    dailySignups: 0,
    systemStatus: 'Loading...',
    activeUsers: 0,
    performance: 'Loading...',
    recentActivity: [],
    websiteList: []
  });
  const [analyticsData, setAnalyticsData] = useState([]);
  const [slowQueriesData, setSlowQueriesData] = useState([]);
  const [consoleSummary, setConsoleSummary] = useState({ total: 0, bySeverity: { high: 0, medium: 0, low: 0 }, recentErrors: [] });
  const [systemData, setSystemData] = useState(null);
  const [, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(externalActiveTab || 'general');
  const [analytics_window_hours, setAnalyticsWindowHours] = useState(24);
  const [threshold_ms, setThresholdMs] = useState(100);

  // Refresh console summary on demand (used by test button and manual refresh)
  const refreshConsoleSummary = async () => {
    try {
      const res = await axios.get(`${config.WORKER_BASE_URL}/api/admin/client-error-summary`);
      setConsoleSummary({
        total: Number(res.data?.total || 0),
        bySeverity: res.data?.bySeverity || { high:0, medium:0, low:0 },
        recentErrors: Array.isArray(res.data?.recentErrors) ? res.data.recentErrors : []
      });
    } catch (e) {
      logger.error('Refresh console summary failed:', e);
    }
  };

  useEffect(() => {
    // Initialize worker base on load (staging/prod autodetect)
    (async () => { try { await initWorkerBase(); } catch(_){} })();
    if (externalActiveTab && externalActiveTab !== activeTab) {
      setActiveTab(externalActiveTab);
    }
  }, [externalActiveTab, activeTab]);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${getWorkerBase()}/api/admin/general`);
      const data = response.data || {};
      setAdminData({
        totalZones: data.totalZones || 0,
        totalCustomers: data.totalCustomers || 0,
        dailySignups: data.dailySignups || 0,
        systemStatus: 'All systems operational',
        activeUsers: (data.websiteList || []).length,
        performance: 'Real-time metrics available',
        recentActivity: data.recentActivity || [],
        websiteList: data.websiteList || []
      });
    } catch (error) {
      logger.error('Error loading admin data:', error);
      setAdminData(prev => ({
        ...prev,
        systemStatus: 'Error loading data'
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadTabData = async () => {
      try {
        if (activeTab === 'analytics') {
          const res = await axios.get(`${getWorkerBase()}/api/query-analytics`, {
            params: { window_hours: String(analytics_window_hours), top: '25', exclude_error_like: 'no such column' }
          });
          setAnalyticsData(Array.isArray(res.data) ? res.data : []);
          const slowRes = await axios.get(`${getWorkerBase()}/api/slow-queries`, {
            params: { window_hours: String(analytics_window_hours), threshold_ms: String(threshold_ms) }
          });
          setSlowQueriesData(Array.isArray(slowRes.data) ? slowRes.data : []);
        } else if (activeTab === 'health') {
          const res = await axios.get(`${getWorkerBase()}/api/admin/system`);
          setSystemData(res.data);
        } else if (activeTab === 'general') {
          await loadAdminData();
        } else if (activeTab === 'console') {
          const res = await axios.get(`${getWorkerBase()}/api/admin/client-error-summary`);
          setConsoleSummary({
            total: Number(res.data?.total || 0),
            bySeverity: res.data?.bySeverity || { high:0, medium:0, low:0 },
            recentErrors: Array.isArray(res.data?.recentErrors) ? res.data.recentErrors : []
          });
        }
      } catch (e) {
        logger.error('Tab load error:', e);
      }
    };
    loadTabData();
  }, [activeTab, analytics_window_hours, threshold_ms]);

  // Auto-refresh when the tab gains focus or the page becomes visible
  useEffect(() => {
    const refreshActive = () => {
      if (activeTab === 'analytics') {
        // Re-run analytics fetch sequence
        (async () => {
          try {
            const res = await axios.get(`${config.WORKER_BASE_URL}/api/query-analytics`, {
              params: { window_hours: String(analytics_window_hours), top: '25', exclude_error_like: 'no such column' }
            });
            setAnalyticsData(Array.isArray(res.data) ? res.data : []);
            const slowRes = await axios.get(`${config.WORKER_BASE_URL}/api/slow-queries`, {
              params: { window_hours: String(analytics_window_hours), threshold_ms: String(threshold_ms) }
            });
            setSlowQueriesData(Array.isArray(slowRes.data) ? slowRes.data : []);
          } catch (e) { /* ignore */ }
        })();
      } else if (activeTab === 'health') {
        axios.get(`${config.WORKER_BASE_URL}/api/admin/system`).then(r=>setSystemData(r.data)).catch(()=>{});
      } else if (activeTab === 'general') {
        loadAdminData();
      } else if (activeTab === 'console') {
        refreshConsoleSummary();
      }
    };
    const onFocus = () => refreshActive();
    const onVisibility = () => { if (document.visibilityState === 'visible') refreshActive(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [activeTab, analytics_window_hours, threshold_ms]);

  const setTab = (tab) => {
    setActiveTab(tab);
    if (onExternalTabChange) onExternalTabChange(tab);
  };

  const renderGeneralTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-900">Total Zones</h3>
          <p className="text-blue-700 text-2xl font-bold">{adminData.totalZones}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="font-semibold text-green-900">Total Customers</h3>
          <p className="text-green-700 text-2xl font-bold">{adminData.totalCustomers}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <h3 className="font-semibold text-purple-900">Daily Signups</h3>
          <p className="text-purple-700 text-2xl font-bold">{adminData.dailySignups}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-900">System Status</h3>
          <p className="text-blue-700 text-sm">{adminData.systemStatus}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="font-semibold text-green-900">Active Users</h3>
          <p className="text-green-700 text-sm">{adminData.activeUsers} users</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <h3 className="font-semibold text-purple-900">Performance</h3>
          <p className="text-purple-700 text-sm">{adminData.performance}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-4 rounded shadow">
          <h4 className="font-semibold mb-3">Recent Activity</h4>
          <ul className="divide-y divide-gray-100">
            {adminData.recentActivity.slice(0, 8).map((item, idx) => (
              <li key={idx} className="py-2 text-sm text-gray-700">
                <span className="font-mono">{item.shop}</span> — {item.action}{item.feature_name ? ` (${item.feature_name})` : ''}
              </li>
            ))}
            {adminData.recentActivity.length === 0 && <li className="py-2 text-sm text-gray-500">No recent activity</li>}
          </ul>
        </div>
        <div className="bg-white p-4 rounded shadow">
          <h4 className="font-semibold mb-3">Latest Websites</h4>
          <ul className="divide-y divide-gray-100">
            {adminData.websiteList.slice(0, 8).map((w, idx) => (
              <li key={idx} className="py-2 text-sm text-gray-700">
                <span className="font-mono">{w.shop}</span>
              </li>
            ))}
            {adminData.websiteList.length === 0 && <li className="py-2 text-sm text-gray-500">No websites yet</li>}
          </ul>
        </div>
      </div>
    </div>
  );

  const renderAnalyticsTab = () => {
    const totalQueries = analyticsData.reduce((s, q) => s + (q.total_queries || 0), 0);
    const avgDuration = analyticsData.length ? (analyticsData.reduce((s, q) => s + (q.avg_duration || 0), 0) / analyticsData.length).toFixed(1) : 0;
    const slowQueries = analyticsData.reduce((s, q) => s + (q.slow_queries || 0), 0);
    const failedQueries = analyticsData.reduce((s, q) => s + (q.failed_queries || 0), 0);
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-end gap-2">
          <span className="text-sm text-gray-500">Window:</span>
          <button className={`nav-button ${analytics_window_hours===1?'active':''}`} onClick={()=>setAnalyticsWindowHours(1)}>Last 1h</button>
          <button className={`nav-button ${analytics_window_hours===24?'active':''}`} onClick={()=>setAnalyticsWindowHours(24)}>Last 24h</button>
          <button className={`nav-button ${analytics_window_hours===168?'active':''}`} onClick={()=>setAnalyticsWindowHours(168)}>Last 7d</button>
          <span className="text-sm text-gray-500" style={{marginLeft:12}}>Threshold (ms):</span>
          <input
            type="number"
            min="1"
            value={threshold_ms}
            onChange={(e)=>setThresholdMs(Math.max(1, Number(e.target.value)||100))}
            style={{ width: 100, padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb' }}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard label="Total Queries" value={totalQueries.toLocaleString()} color="blue" />
          <StatCard label="Avg Duration" value={`${avgDuration}ms`} color="green" />
          <StatCard label="Slow Queries" value={slowQueries.toLocaleString()} color="orange" />
          <StatCard label="Failed Queries" value={failedQueries.toLocaleString()} color="red" />
        </div>
        <div className="bg-white p-4 rounded shadow">
          <h4 className="font-semibold mb-3">Top Queries (by usage)</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-2 pr-6">Query</th>
                  <th className="py-2 pr-6">Total</th>
                  <th className="py-2 pr-6">Avg (ms)</th>
                  <th className="py-2 pr-6">Max (ms)</th>
                  <th className="py-2 pr-6">Slow</th>
                  <th className="py-2 pr-6">Failed</th>
                </tr>
              </thead>
              <tbody>
                {analyticsData.slice(0, 10).map((q, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-2 pr-6 font-mono">{q.query_name}</td>
                    <td className="py-2 pr-6">{q.total_queries}</td>
                    <td className="py-2 pr-6">{(q.avg_duration||0).toFixed(1)}</td>
                    <td className="py-2 pr-6">{q.max_duration}</td>
                    <td className="py-2 pr-6">{q.slow_queries}</td>
                    <td className="py-2 pr-6">{q.failed_queries}</td>
                  </tr>
                ))}
                {analyticsData.length === 0 && (
                  <tr><td className="py-3 text-gray-500" colSpan="6">No analytics yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-white p-4 rounded shadow">
          <h4 className="font-semibold mb-3">Slow/Failed Queries (>{threshold_ms}ms, last {analytics_window_hours}h)</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-2 pr-6">Query</th>
                  <th className="py-2 pr-6">Duration (ms)</th>
                  <th className="py-2 pr-6">Params</th>
                  <th className="py-2 pr-6">Time</th>
                  <th className="py-2 pr-6">Status</th>
                </tr>
              </thead>
              <tbody>
                {slowQueriesData.slice(0, 25).map((q,i)=>{
                  const failed = !!q.error_message;
                  return (
                    <tr key={i} className="border-top">
                      <td className="py-2 pr-6 font-mono">{q.query_name}</td>
                      <td className="py-2 pr-6">{q.duration_ms}</td>
                      <td className="py-2 pr-6">{q.params_count}</td>
                      <td className="py-2 pr-6">{new Date(q.created_at).toLocaleString()}</td>
                      <td className={`py-2 pr-6 ${failed?'text-red-600':'text-green-600'}`}>{failed?'Failed':'OK'}</td>
                    </tr>
                  )
                })}
                {slowQueriesData.length === 0 && (
                  <tr><td className="py-3 text-gray-500" colSpan="5">No slow/failed queries in window</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const StatCard = ({ label, value, color }) => (
    <div className={`bg-${color}-50 p-4 rounded-lg`}>
      <h3 className={`font-semibold text-${color}-900`}>{label}</h3>
      <p className={`text-${color}-700 text-2xl font-bold`}>{value}</p>
    </div>
  );

  const renderHealthTab = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">System Health</h3>
        {systemData ? (
          <ul className="space-y-2">
            {(systemData.systemHealth || []).map((row, idx) => (
              <li key={idx} className="flex justify-between bg-green-50 rounded p-3">
                <span className="font-medium">{row.component}</span>
                <span className="text-green-700 font-semibold">{row.status}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500 text-sm">Load System Health to view details</p>
        )}
      </div>
    </div>
  );

  const renderConsoleTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-end gap-2">
        <button
          className="nav-button"
          onClick={async ()=>{
            try {
              await axios.post(`${config.WORKER_BASE_URL}/api/admin/error-track`, {
                id: `test_${Date.now()}`,
                error: 'Test admin console error',
                severity: 'low',
                context: { page: '/admin/console-errors', action: 'manual_test' }
              });
              setTimeout(()=>{ refreshConsoleSummary(); }, 400);
            } catch(e){ logger.error('Test error generation failed:', e); }
          }}
        >Generate Test Error</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard label="Total" value={consoleSummary.total} color="blue" />
        <StatCard label="High" value={consoleSummary.bySeverity.high} color="red" />
        <StatCard label="Medium" value={consoleSummary.bySeverity.medium} color="orange" />
        <StatCard label="Low" value={consoleSummary.bySeverity.low} color="green" />
      </div>
      <div className="bg-white p-4 rounded shadow">
        <h4 className="font-semibold mb-3">Recent Console Errors</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-6">Time</th>
                <th className="py-2 pr-6">Severity</th>
                <th className="py-2 pr-6">Page</th>
                <th className="py-2 pr-6">Action</th>
                <th className="py-2 pr-6">Message</th>
              </tr>
            </thead>
            <tbody>
              {consoleSummary.recentErrors.map((r, i) => (
                <tr key={i} className="border-t">
                  <td className="py-2 pr-6">{new Date(r.created_at).toLocaleString()}</td>
                  <td className={`py-2 pr-6 ${r.severity==='high'?'text-red-600': r.severity==='medium'?'text-orange-600':'text-green-700'}`}>{r.severity}</td>
                  <td className="py-2 pr-6">{r.page}</td>
                  <td className="py-2 pr-6">{r.action}</td>
                  <td className="py-2 pr-6">{r.message}</td>
                </tr>
              ))}
              {consoleSummary.recentErrors.length === 0 && (
                <tr><td className="py-3 text-gray-500" colSpan="5">No errors recorded</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderUsersTab = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">User Management</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <span className="font-medium">Total Users</span>
            <span className="font-semibold">{adminData.totalCustomers}</span>
          </div>
          <AdminUserMetrics />
        </div>
      </div>
    </div>
  );

  const AdminUserMetrics = () => {
    const [metrics, setMetrics] = useState({ free_plan: 0, paid_plan: 0, basic_plan: 0, pro_plan: 0 });
    const [loadingMetrics, setLoadingMetrics] = useState(true);

    useEffect(() => {
      let cancelled = false;
      const load = async () => {
        try {
          setLoadingMetrics(true);
          const res = await axios.get(`${config.WORKER_BASE_URL}/api/admin/customers-summary`);
          if (!cancelled) setMetrics(res.data || {});
        } catch (e) {
          if (!cancelled) setMetrics({ free_plan: 0, paid_plan: 0 });
        } finally {
          if (!cancelled) setLoadingMetrics(false);
        }
      };
      load();
      return () => { cancelled = true; };
    }, []);

    return (
      <>
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
          <span className="font-medium">Active Subscriptions</span>
          <span className="font-semibold">{loadingMetrics ? 'Loading...' : (metrics.paid_plan || 0)}</span>
        </div>
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
          <span className="font-medium">Free Plan Users</span>
          <span className="font-semibold">{loadingMetrics ? 'Loading...' : (metrics.free_plan || 0)}</span>
        </div>
      </>
    );
  };

  // Tab handlers are driven directly by onClick props; no standalone functions needed

  return (
    <div className="admin-dashboard min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">CloudCache Admin Dashboard</h1>
              <p className="text-gray-600">Welcome to the CloudCache administration panel</p>
            </div>
          </div>
          
          {/* Tab Navigation */}
          {!hideTabs && (
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex space-x-8">
                <button 
                  onClick={() => setTab('general')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'general' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  General
                </button>
                <button 
                  onClick={() => setTab('analytics')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'analytics' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Database Performance
                </button>
                <button 
                  onClick={() => setTab('health')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'health' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  System Health
                </button>
                <button 
                  onClick={() => setTab('users')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'users' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  User Management
                </button>
                <button 
                  onClick={() => setTab('console')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'console' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Console Errors
                </button>
              </nav>
            </div>
          )}

          {/* Tab Content */}
          {activeTab === 'general' && renderGeneralTab()}
          {activeTab === 'analytics' && renderAnalyticsTab()}
          {activeTab === 'health' && renderHealthTab()}
          {activeTab === 'users' && renderUsersTab()}
          {activeTab === 'console' && renderConsoleTab()}
          
          {/* Quick actions removed for admin backend */}
        </div>
      </div>
    </div>
  );
};

function App() {
  const [shop_domain, setShopDomain] = useState('');
  const [current_page, setCurrentPage] = useState('registration');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [user_plan, setUserPlan] = useState('free'); // free, basic, pro
  // Tracks whether OAuth completed; not currently used to drive UI
  // eslint-disable-next-line no-unused-vars
  const [is_connected, setIsConnected] = useState(false);
  const [admin_tab, setAdminTab] = useState('general');
  
  // Home page toggles (5 total)
  const [home_toggles, setHomeToggles] = useState({
    rocket_loader: get_toggle_state(shop_domain, 'rocket_loader', false),
    early_hints: get_toggle_state(shop_domain, 'early_hints', false),
    brotli: get_toggle_state(shop_domain, 'brotli', false),
    minify: get_toggle_state(shop_domain, 'minify', false),
    http2: get_toggle_state(shop_domain, 'http2', false)
  });

  // Section pages (5 pages with 10 toggles each + master toggle)
  const [section_toggles, setSectionToggles] = useState({
    A: {
      master: false,
      toggle_01: false,
      toggle_02: false,
      toggle_03: false,
      toggle_04: false,
      toggle_05: false,
      toggle_06: false,
      toggle_07: false,
      toggle_08: false,
      toggle_09: false,
      toggle_10: false
    },
    B: {
      master: false,
      toggle_01: false,
      toggle_02: false,
      toggle_03: false,
      toggle_04: false,
      toggle_05: false,
      toggle_06: false,
      toggle_07: false,
      toggle_08: false,
      toggle_09: false,
      toggle_10: false
    },
    C: {
      master: false,
      toggle_01: false,
      toggle_02: false,
      toggle_03: false,
      toggle_04: false,
      toggle_05: false,
      toggle_06: false,
      toggle_07: false,
      toggle_08: false,
      toggle_09: false,
      toggle_10: false
    },
    D: {
      master: false,
      toggle_01: false,
      toggle_02: false,
      toggle_03: false,
      toggle_04: false,
      toggle_05: false,
      toggle_06: false,
      toggle_07: false,
      toggle_08: false,
      toggle_09: false,
      toggle_10: false
    },
    E: {
      master: false,
      toggle_01: false,
      toggle_02: false,
      toggle_03: false,
      toggle_04: false,
      toggle_05: false,
      toggle_06: false,
      toggle_07: false,
      toggle_08: false,
      toggle_09: false,
      toggle_10: false
    }
  });

  useEffect(() => {
    // Parse URL parameters (OAuth)
    const url_params = new URLSearchParams(window.location.search);
    const code = url_params.get('code');
    const state = url_params.get('state');
    const shop = url_params.get('shop');

    // Restore from clean path
    const { page: pathPage, tab: pathTab } = parsePath(window.location.pathname);
    if (pathPage) setCurrentPage(pathPage);
    if (pathTab) setAdminTab(pathTab);

    // Handle OAuth callback
    if (code && state && shop) {
      check_oauth_callback(code, state, shop);
      return;
    }

    if (shop) {
      setShopDomain(shop);
      const initiated_key = `oauth_started:${shop}`;
      if (!sessionStorage.getItem(initiated_key)) {
        sessionStorage.setItem(initiated_key, '1');
        initiate_oauth(shop);
      }
      return;
    }
  }, []);

  // Keep path in sync with state
  useEffect(() => {
    if (current_page === 'admin') {
      const p = adminTabToPath(admin_tab);
      if (window.location.pathname !== p) window.history.replaceState({}, document.title, p);
    } else {
      const p = pageToPath(current_page);
      if (window.location.pathname !== p) window.history.replaceState({}, document.title, p);
    }
  }, [current_page, admin_tab]);

  // Kept for future use post-onboarding; intentionally not auto-invoked before setup
  // eslint-disable-next-line no-unused-vars
  const load_rocket_loader_status = async (shop) => {
    try {
      const response = await axios.get(`${config.WORKER_BASE_URL}/api/rocket-loader-status?shop=${shop}`);
      if (response.data.success) {
        const is_on = response.data.result.value === 'on';
        setHomeToggles(prev => ({
          ...prev,
          rocket_loader: is_on
        }));
        set_toggle_state(shop, 'rocket_loader', is_on);
        logger.log('Rocket Loader status loaded:', response.data.result.value);
      } else {
        logger.log('Rocket Loader status not available:', response.data.error);
        // Don't show error message for expected onboarding state
      }
    } catch (error) {
      // Only log errors that aren't expected onboarding states
      if (error.response?.status !== 400 || !error.response?.data?.error?.includes('onboarding')) {
        logger.error('Error loading Rocket Loader status:', error);
      } else {
        logger.log('Rocket Loader status not available - onboarding required');
      }
      // Keep the current state if we can't load it
    }
  };

  // Kept for future use post-onboarding; intentionally not auto-invoked before setup
  // eslint-disable-next-line no-unused-vars
  const load_user_plan = async (shop) => {
    try {
      // Load user's current plan from our backend
      const response = await axios.get(`${config.WORKER_BASE_URL}/api/user-plan?shop=${shop}`);
      if (response.data.success) {
        setUserPlan(response.data.plan || 'free');
        logger.log('User plan loaded:', response.data.plan);
      } else {
        logger.log('No plan found, defaulting to free');
        setUserPlan('free');
      }
    } catch (error) {
      // Only log errors that aren't expected onboarding states
      if (error.response?.status !== 400 || !error.response?.data?.error?.includes('onboarding')) {
        logger.error('Error loading user plan:', error);
      } else {
        logger.log('User plan not available - onboarding required');
      }
      setUserPlan('free');
    }
  };

  const initiate_oauth = async (shop) => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.WORKER_BASE_URL}/api/auth/shopify?shop=${shop}`);
      if (response.data.authUrl) {
        // Redirect to Shopify OAuth
        window.location.href = response.data.authUrl;
      }
    } catch (error) {
      logger.error('Error initiating OAuth:', error);
      setMessage('Failed to connect to Shopify');
    } finally {
      setLoading(false);
    }
  };

  const check_oauth_callback = async (code, state, shop) => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.WORKER_BASE_URL}/api/auth/callback?code=${code}&shop=${shop}&state=${state}`);
      if (response.data.success) {
        setIsConnected(true);
        setShopDomain(shop);
        setMessage('Successfully connected to Shopify!');
        // Clean up URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (error) {
      logger.error('OAuth callback error:', error);
      setMessage('Failed to complete authentication');
    } finally {
      setLoading(false);
    }
  };

  const handle_home_toggle = async (toggle_name, enabled) => {
    setLoading(true);
    setMessage('');

    try {
      // Require connected shop before calling backend
      if (!shop_domain) {
        setMessage('Please connect your store first (Quick Start) to enable backend toggles.');
        return;
      }
      if (toggle_name === 'rocket_loader') {
        // Use existing Rocket Loader API
        const response = await axios.post(`${config.WORKER_BASE_URL}/api/toggle-rocket-loader`, {
          shop: shop_domain,
          enabled: enabled
        });

        if (response.data.success) {
          setHomeToggles(prev => ({
            ...prev,
            rocket_loader: enabled
          }));
          set_toggle_state(shop_domain, 'rocket_loader', enabled);
          setMessage(`${enabled ? 'Enabled' : 'Disabled'} Rocket Loader successfully!`);
        } else {
          setMessage('Failed to toggle Rocket Loader.');
        }
      } else {
        // Mock toggle for other features (same logic as Rocket Loader but without API call)
        setHomeToggles(prev => ({
          ...prev,
          [toggle_name]: enabled
        }));
        set_toggle_state(shop_domain, toggle_name, enabled);
        setMessage(`${enabled ? 'Enabled' : 'Disabled'} ${toggle_name.replace('_', ' ')} successfully!`);
      }
    } catch (error) {
      // Suppress expected onboarding 400 errors
      const api_msg = error?.response?.data?.error || '';
      if (error?.response?.status === 400 && api_msg.includes('onboarding')) {
        setMessage('Onboarding required: map your store to a zone before toggling.');
      } else {
        logger.error('Error toggling feature:', error);
        errorTracker.trackError(error, {
          page: 'home',
          action: 'toggle_feature',
          feature: toggle_name
        });
        setMessage('Error toggling feature. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handle_section_toggle = async (section, toggle_name, enabled) => {
    setLoading(true);
    setMessage('');

    try {
      if (toggle_name === 'master') {
        // Master toggle - toggle all features in the section
        const new_toggles = {};
        Object.keys(section_toggles[section]).forEach(key => {
          if (key !== 'master') {
            new_toggles[key] = enabled;
          }
        });
        new_toggles.master = enabled;

        setSectionToggles(prev => ({
          ...prev,
          [section]: new_toggles
        }));
        setMessage(`${enabled ? 'Enabled' : 'Disabled'} all features in Section ${section}!`);
      } else {
        // Individual toggle
        setSectionToggles(prev => ({
          ...prev,
          [section]: {
            ...prev[section],
            [toggle_name]: enabled
          }
        }));
        setMessage(`${enabled ? 'Enabled' : 'Disabled'} ${toggle_name.replace('_', ' ')} successfully!`);
      }
    } catch (error) {
      logger.error('Error toggling section feature:', error);
      errorTracker.trackError(error, {
        page: `section_${section}`,
        function: 'toggle_section_feature',
        feature: toggle_name
      });
      setMessage('Error toggling feature. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Shopify Subscriptions API Integration
  const create_subscription = async (plan_id) => {
    setLoading(true);
    setMessage('');

    try {
      // Request billing confirmation URL (Shopify Billing API)
      const price_map = { free: 0, basic: 29, pro: 59 };
      const response = await axios.post(`${config.WORKER_BASE_URL}/api/billing/start`, {
        shop: shop_domain,
        plan_name: pricing_plans.find(p => p.id === plan_id)?.name || plan_id,
        price: price_map[plan_id]
      });

      if (response.data?.success && response.data?.confirmationUrl) {
        window.location.href = response.data.confirmationUrl;
        return;
      }

      setMessage(response.data?.error || 'Failed to start billing.');
    } catch (error) {
      logger.error('Error creating subscription:', error);
      setMessage('Error creating subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cancel_subscription = async () => {
    setLoading(true);
    setMessage('');

    try {
      const response = await axios.post(`${config.WORKER_BASE_URL}/api/cancel-subscription`, {
        shop: shop_domain
      });

      if (response.data.success) {
        setUserPlan('free');
        setMessage('Subscription cancelled successfully.');
        
        // Log cancellation event
        await axios.post(`${config.WORKER_BASE_URL}/api/log-subscription`, {
          shop: shop_domain,
          plan_id: user_plan,
          action: 'cancel'
        });
      } else {
        setMessage(response.data.error || 'Failed to cancel subscription.');
      }
    } catch (error) {
      logger.error('Error cancelling subscription:', error);
      setMessage('Error cancelling subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const home_page_toggles = [
    {
      name: 'rocket_loader',
      label: 'Rocket Loader',
      description: 'Automatically optimizes your website\'s loading performance by deferring JavaScript execution.'
    },
    {
      name: 'early_hints',
      label: 'Early Hints',
      description: 'Sends HTTP 103 responses to preload critical resources before the main response.'
    },
    {
      name: 'brotli',
      label: 'Brotli Compression',
      description: 'Uses Brotli compression algorithm for better compression ratios and faster loading.'
    },
    {
      name: 'minify',
      label: 'Auto Minify',
      description: 'Automatically minifies CSS, JavaScript, and HTML to reduce file sizes.'
    },
    {
      name: 'http2',
      label: 'HTTP/2',
      description: 'Enables HTTP/2 protocol for improved performance and multiplexing.'
    }
  ];

  const section_page_toggles = [
    {
      name: 'toggle_01',
      label: 'Toggle 01',
      description: 'Description goes here...'
    },
    {
      name: 'toggle_02',
      label: 'Toggle 02',
      description: 'Description goes here...'
    },
    {
      name: 'toggle_03',
      label: 'Toggle 03',
      description: 'Description goes here...'
    },
    {
      name: 'toggle_04',
      label: 'Toggle 04',
      description: 'Description goes here...'
    },
    {
      name: 'toggle_05',
      label: 'Toggle 05',
      description: 'Description goes here...'
    },
    {
      name: 'toggle_06',
      label: 'Toggle 06',
      description: 'Description goes here...'
    },
    {
      name: 'toggle_07',
      label: 'Toggle 07',
      description: 'Description goes here...'
    },
    {
      name: 'toggle_08',
      label: 'Toggle 08',
      description: 'Description goes here...'
    },
    {
      name: 'toggle_09',
      label: 'Toggle 09',
      description: 'Description goes here...'
    },
    {
      name: 'toggle_10',
      label: 'Toggle 10',
      description: 'Description goes here...'
    }
  ];

  const navigation_buttons = [
    { id: 'registration', label: 'Quick Start' },
    { id: 'home', label: 'Free Plan' },
    { id: 'A', label: 'Section A' },
    { id: 'B', label: 'Section B' },
    { id: 'C', label: 'Section C' },
    { id: 'D', label: 'Section D' },
    { id: 'E', label: 'Section E' },
    { id: 'pricing', label: 'Pricing' }
  ];

  const pricing_plans = [
    {
      id: 'free',
      name: 'Free Plan',
      price: '$0',
      period: 'forever',
      features: [
        'Access to Home page features',
        'Rocket Loader optimization',
        'Basic performance monitoring',
        'Email support',
        'Up to 1,000 monthly visitors'
      ],
              button_text: 'Current Plan',
              button_action: () => setMessage('You are already on the Free plan!'),
      popular: false,
              shopify_plan_id: null
    },
    {
      id: 'basic',
      name: 'Basic Plan',
      price: '$29',
      period: 'per month',
      features: [
        'Everything in Free plan',
        'Access to Sections A, B, C',
        'Advanced performance features',
        'Priority email support',
        'Up to 10,000 monthly visitors'
      ],
              button_text: user_plan === 'basic' ? 'Current Plan' : 'Subscribe',
              button_action: () => user_plan === 'basic' ? setMessage('You are already on the Basic plan!') : create_subscription('basic'),
      popular: true,
              shopify_plan_id: 'basic_monthly_29'
    },
    {
      id: 'pro',
      name: 'Pro Plan',
      price: '$59',
      period: 'per month',
      features: [
        'Everything in Basic plan',
        'Access to Sections D, E',
        'All Cloudflare features',
        'Priority phone support',
                'Unlimited monthly visitors'
        ],
        button_text: user_plan === 'pro' ? 'Current Plan' : 'Subscribe',
        button_action: () => user_plan === 'pro' ? setMessage('You are already on the Pro plan!') : create_subscription('pro'),
      popular: false,
              shopify_plan_id: 'pro_monthly_59'
    }
  ];

  // Check if user has access to a section based on their plan
  const has_section_access = (section) => {
    switch (user_plan) {
      case 'free':
        return false; // No sections for free plan
      case 'basic':
        return ['A', 'B', 'C'].includes(section);
      case 'pro':
        return ['A', 'B', 'C', 'D', 'E'].includes(section);
      default:
        return false;
    }
  };

  const render_toggle = (toggle_name, label, description, enabled, onToggle, is_master = false, disabled = false) => (
    <div key={toggle_name} className={`toggle-item ${is_master ? 'master-toggle' : ''} ${disabled ? 'disabled' : ''}`}>
      <div className="toggle-header">
        <h3 className="toggle-label">{label}</h3>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onToggle(toggle_name, e.target.checked)}
            disabled={loading || disabled}
          />
          <span className="toggle-slider"></span>
        </label>
        <div className="toggle-badges">
          {disabled ? (
            <span className="badge badge--disabled">Disabled</span>
          ) : (
            <span className="badge badge--synced">Synced</span>
          )}
        </div>
      </div>
      <p className="toggle-description">{description}</p>
      {disabled && (
        <div className="upgrade-notice">
          <span className="upgrade-icon">🔒</span>
          <span>Upgrade your plan to access this feature</span>
        </div>
      )}
    </div>
  );

  const goToPage = (pageId) => {
    setCurrentPage(pageId);
    if (pageId !== 'admin') {
      const p = pageToPath(pageId);
      if (window.location.pathname !== p) window.history.pushState({}, document.title, p);
    }
  };
  const goToAdminTab = (tabId) => {
    setAdminTab(tabId);
    const p = adminTabToPath(tabId);
    if (window.location.pathname !== p) window.history.pushState({}, document.title, p);
  };

  const render_navigation = () => (
    <div className="navigation-bar">
      {current_page !== 'admin' ? (
        navigation_buttons.map(button => (
          <button
            key={button.id}
            className={`nav-button ${current_page === button.id ? 'active' : ''}`}
            onClick={() => goToPage(button.id)}
          >
            {button.label}
          </button>
        ))
      ) : (
        <div className="admin-nav-buttons">
          <button className={`nav-button`} onClick={() => goToPage('registration')}>View App</button>
          <button className={`nav-button ${admin_tab === 'general' ? 'active' : ''}`} onClick={() => goToAdminTab('general')}>General</button>
          <button className={`nav-button ${admin_tab === 'analytics' ? 'active' : ''}`} onClick={() => goToAdminTab('analytics')}>Database Performance</button>
          <button className={`nav-button ${admin_tab === 'health' ? 'active' : ''}`} onClick={() => goToAdminTab('health')}>System Health</button>
          <button className={`nav-button ${admin_tab === 'users' ? 'active' : ''}`} onClick={() => goToAdminTab('users')}>User Management</button>
          <button className={`nav-button ${admin_tab === 'console' ? 'active' : ''}`} onClick={() => goToAdminTab('console')}>Console Errors</button>
        </div>
      )}
    </div>
  );

  const render_home_page = () => (
    <div className="page-content">
      <div className="page-header">
        <h1>CloudCache Free Plan</h1>
                  <p className="shop-info">Store: {shop_domain || 'None'}</p>
        <p className="plan-info">Current Plan: {user_plan.charAt(0).toUpperCase() + user_plan.slice(1)}</p>
        <p className="plan-description">
          Welcome to your CloudCache Free Plan! This plan includes access to basic features and free plan toggles.
          Upgrade to Basic or Pro plans to unlock additional toggle sections and advanced capabilities.
          <br /><br />
          <a href="/pricing" onClick={(e)=>{e.preventDefault(); goToPage('pricing');}} style={{ color: '#f97316', textDecoration: 'underline' }}>
            Review our plan features → See Pricing
          </a>
        </p>


      </div>
      
      <div className="toggle-list">
        {home_page_toggles.map(toggle => {
          // Enable only when the app is fully connected (OAuth done) and the toggle is implemented
          const is_supported = (toggle.name === 'rocket_loader') && !!is_connected;
          const value = is_supported ? home_toggles[toggle.name] : false;
          return render_toggle(
            toggle.name,
            toggle.label,
            toggle.description,
            value,
            handle_home_toggle,
            false,
            !is_supported // disabled when not supported/connected
          );
        })}
      </div>

      {message && <div className="message">{message}</div>}


    </div>
  );

  const render_section_page = (section) => {
    const has_access = has_section_access(section);
    
    return (
      <div className="page-content">
        <div className="page-header">
          <h1>Toggle Section {section}</h1>
          <p className="shop-info">Store: {shop_domain || 'Not Connected'}</p>
          <p className="plan-info">Current Plan: {user_plan.charAt(0).toUpperCase() + user_plan.slice(1)}</p>
          {!has_access && (
            <div className="upgrade-banner">
              <span>🔒 This section requires a higher plan. </span>
              <button 
                className="upgrade-button"
                onClick={() => setCurrentPage('pricing')}
              >
                Upgrade Now
              </button>
            </div>
          )}
        </div>

        <div className="toggle-list">
          {/* Master Toggle */}
          {render_toggle(
            'master',
            `Master Toggle - Section ${section}`,
            `Toggle all features in Section ${section} on or off`,
            section_toggles[section].master,
            (toggle_name, enabled) => handle_section_toggle(section, toggle_name, enabled),
            true,
            !has_access
          )}

          {/* Individual Toggles */}
          {section_page_toggles.map(toggle => 
            render_toggle(
              toggle.name,
              toggle.label,
              toggle.description,
              section_toggles[section][toggle.name],
              (toggle_name, enabled) => handle_section_toggle(section, toggle_name, enabled),
              false,
              !has_access
            )
          )}
        </div>

        {message && <div className="message">{message}</div>}


      </div>
    );
  };

  const render_pricing_page = () => (
    <div className="page-content">
      <div className="page-header">
        <h1>Choose Your Plan</h1>
        <p className="shop-info">Store: {shop_domain || 'Not Connected'}</p>
        <p className="plan-info">Current Plan: {user_plan.charAt(0).toUpperCase() + user_plan.slice(1)}</p>
        <p className="pricing-subtitle">Select the perfect plan for your business needs</p>
        {user_plan !== 'free' && (
          <div className="current-plan-banner">
            <span>Current Plan: {user_plan.charAt(0).toUpperCase() + user_plan.slice(1)}</span>
            <button 
              className="btn btn--danger btn--sm"
              onClick={cancel_subscription}
              disabled={loading}
            >
              Cancel Subscription
            </button>
          </div>
        )}
      </div>

      <div className="pricing-grid">
        {pricing_plans.map(plan => (
          <div key={plan.id} className={`pricing-card ${plan.popular ? 'popular' : ''} ${user_plan === plan.id ? 'current' : ''}`}>
            {plan.popular && <div className="popular-badge">Most Popular</div>}
            {user_plan === plan.id && <div className="current-badge">Current Plan</div>}
            
            <div className="plan-header">
              <h2 className="plan-name">{plan.name}</h2>
              <div className="plan-price">
                <span className="price">{plan.price}</span>
                <span className="period">/{plan.period}</span>
              </div>
            </div>

            <div className="plan-features">
              {plan.features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span className="feature-text">{feature}</span>
                </div>
              ))}
            </div>

            <button 
              className={`btn ${user_plan === plan.id ? 'btn--green' : 'btn--primary'} btn--full`}
              onClick={plan.button_action}
              disabled={loading}
            >
              {loading ? 'Processing...' : plan.button_text}
            </button>
          </div>
        ))}
      </div>

      {message && <div className="message">{message}</div>}
    </div>
  );

  return (
    <div className="App">
      {render_navigation()}
      {current_page === 'home' && render_home_page()}
      {current_page === 'registration' && <Registration />}
      {['A', 'B', 'C', 'D', 'E'].includes(current_page) && render_section_page(current_page)}
      {current_page === 'pricing' && render_pricing_page()}
      {current_page === 'admin' && (
        <AdminDashboard
          externalActiveTab={admin_tab}
          onExternalTabChange={setAdminTab}
          hideTabs={true}
        />
      )}
    </div>
  );
}

export default App; 